import loadTimetableDate from '../timetable_date';
import syncCategoryDisclosures from '../timetable_categories';

export default () => {
  const FILTER_PARAMS = ['category', 'subcategory', 'session', 'time_of_day', 'free_spaces'];
  // The three activity filters constrain one another: each one offers only the
  // options the other two still leave a session for.
  const FILTERS = [
    { key: 'cat', target: 'category', data: 'category', param: 'category' },
    { key: 'sub', target: 'subcategory', data: 'subcategory', param: 'subcategory' },
    { key: 'ses', target: 'session', data: 'session', param: 'session' },
  ];
  const EMPTY_COPY =
    '<p class="weekly-timetable__empty-title">No sessions found at this centre</p>' +
    '<p class="weekly-timetable__empty-subtitle">Please try expanding your search filters or selecting a different date.</p>';

  function timeBand(hour) {
    if (hour <= 11) return 'morning';
    if (hour <= 17) return 'afternoon';
    return 'evening';
  }

  function allRows(el) {
    return [...el.querySelectorAll('.weekly-timetable__row[data-category]')];
  }

  function getSelects(el) {
    return {
      cat: el.querySelector('[data-timetable-filters-target="category"]'),
      sub: el.querySelector('[data-timetable-filters-target="subcategory"]'),
      ses: el.querySelector('[data-timetable-filters-target="session"]'),
      tod: el.querySelector('[data-timetable-filters-target="timeOfDay"]'),
      spaces: el.querySelector('[data-timetable-filters-target="freeSpaces"]'),
      reset: el.querySelector('[data-timetable-filters-target="reset"]'),
    };
  }

  function storedCombinations(el) {
    try {
      return JSON.parse(el.dataset.timetableFiltersCombinations || 'null');
    } catch (e) {
      return null;
    }
  }

  // A row only describes the day on screen, so it is the fallback for a page
  // rendered without the centre's stored list.
  function rowCombinations(el) {
    return allRows(el).map(row => ({
      category: row.dataset.category || null,
      subcategory: row.dataset.subcategory || null,
      session: row.dataset.session || null,
    }));
  }

  // Taken once: changing the day swaps the rows, and the option sets are the
  // centre's, not the day's.
  function combinations(el) {
    if (!el._filterCombinations) {
      el._filterCombinations = storedCombinations(el) || rowCombinations(el);
    }
    return el._filterCombinations;
  }

  function selectedValues(el) {
    const selects = getSelects(el);
    return FILTERS.reduce((values, { key }) => (
      { ...values, [key]: selects[key] ? selects[key].value : '' }
    ), {});
  }

  function updateSelectState(sel) {
    if (!sel) return;
    sel.classList.toggle('timetable-filters__select--active', sel.value !== '');
  }

  function sortedValues(el, data) {
    const values = new Set();
    combinations(el).forEach(combo => { if (combo[data]) values.add(combo[data]); });
    return [...values].sort();
  }

  // The full option set, kept so a filter can be widened back out once the
  // others stop constraining it. The sub-category list is the one the server
  // cannot render - it belongs to whichever category is picked - so it is cut
  // from the combinations instead.
  function optionSnapshot(el) {
    if (!el._filterOptions) {
      el._filterOptions = {};
      FILTERS.forEach(({ key, data }) => {
        const options = getSelects(el)[key] ? [...getSelects(el)[key].options] : [];
        el._filterOptions[key] = {
          placeholder: options.length ? options[0].textContent : '',
          values: options.length > 1 ? options.slice(1).map(o => o.value) : sortedValues(el, data),
        };
      });
    }
    return el._filterOptions;
  }

  function matchesOthers(combo, key, values) {
    return FILTERS.every(({ key: other, data }) =>
      other === key || !values[other] || combo[data] === values[other]);
  }

  // Sub-category stays gated behind a category, so it offers nothing until one
  // is chosen.
  function availableFor(el, key, values) {
    const available = new Set();
    if (key === 'sub' && !values.cat) return available;

    combinations(el).forEach(combo => {
      const value = combo[FILTERS.find(f => f.key === key).data];
      if (value && matchesOthers(combo, key, values)) available.add(value);
    });
    return available;
  }

  function renderOptions(el, key, available) {
    const sel = getSelects(el)[key];
    if (!sel) return;
    const { placeholder, values } = optionSnapshot(el)[key];
    const current = sel.value;
    sel.textContent = '';
    sel.appendChild(new Option(placeholder, ''));
    values.filter(value => available.has(value))
          .forEach(value => sel.appendChild(new Option(value, value)));
    sel.value = [...sel.options].some(option => option.value === current) ? current : '';
    updateSelectState(sel);
  }

  // A clash between two selections is settled in favour of the filters above:
  // narrowing the category clears a session it does not run, never the reverse.
  function allowedFor(el, key, values) {
    const above = FILTERS.slice(0, FILTERS.findIndex(f => f.key === key));
    return availableFor(el, key, above.reduce((scope, filter) => (
      { ...scope, [filter.key]: values[filter.key] }
    ), {}));
  }

  function clearIfUnavailable(el, key, available) {
    const sel = getSelects(el)[key];
    if (!sel || !sel.value || available.has(sel.value)) return;
    sel.value = '';
  }

  // Narrowing one filter can invalidate another's selection, and clearing that
  // selection widens the first again, so every selection is settled before any
  // option list is rendered.
  function constrainFilters(el) {
    FILTERS.forEach(({ key }) => {
      const values = selectedValues(el);
      clearIfUnavailable(el, key, allowedFor(el, key, values));
    });
    FILTERS.forEach(({ key }) => {
      const values = selectedValues(el);
      renderOptions(el, key, availableFor(el, key, values));
    });
    const { sub } = getSelects(el);
    if (sub) sub.disabled = !selectedValues(el).cat;
    return selectedValues(el);
  }

  function rowMatches(row, values, todVal, onlyFree) {
    const hour = parseInt(row.dataset.hour, 10);
    return (
      (!values.cat || row.dataset.category === values.cat) &&
      (!values.sub || row.dataset.subcategory === values.sub) &&
      (!values.ses || row.dataset.session === values.ses) &&
      (!todVal || timeBand(hour) === todVal) &&
      (!onlyFree || parseInt(row.dataset.spaces, 10) > 0)
    );
  }

  function applyFilters(el) {
    const { tod, spaces } = getSelects(el);
    const values = constrainFilters(el);
    const todVal = tod?.value || '';
    const onlyFree = spaces?.checked || false;

    let anyVisible = false;

    allRows(el).forEach(row => {
      const match = rowMatches(row, values, todVal, onlyFree);

      row.style.display = match ? '' : 'none';

      const desc = row.nextElementSibling;
      if (desc?.classList.contains('weekly-timetable__row-desc')) {
        desc.style.display = match ? '' : 'none';
      }
    });

    el.querySelectorAll('.weekly-timetable__category:not(.timetable-filters__empty-category)').forEach(section => {
      const hasVisible = allRows(section).some(r => r.style.display !== 'none');
      section.style.display = hasVisible ? '' : 'none';
      if (hasVisible) anyVisible = true;
    });

    updateEmptyState(el, anyVisible, values.cat);
    syncCategoryDisclosures(el, Object.keys(collectFilterParams(el)).length > 0);
  }

  function emptyCategory(el) {
    let node = el.querySelector('.timetable-filters__empty-category');
    if (node) return node;

    node = document.createElement('div');
    node.className = 'weekly-timetable__category timetable-filters__empty-category';
    node.innerHTML =
      '<div class="weekly-timetable__category-header">' +
        '<span class="weekly-timetable__category-name"></span>' +
        '<span class="weekly-timetable__category-line"></span>' +
        '<span class="weekly-timetable__category-count">0 sessions</span>' +
      '</div>' +
      `<div class="weekly-timetable__empty">${EMPTY_COPY}</div>`;
    el.querySelector('.weekly-timetable__sessions')?.appendChild(node);
    return node;
  }

  // A day the server found empty already carries the message, so that block
  // is taken over rather than doubled up with a second one beneath it.
  function serverEmptyNode(el) {
    return el.querySelector(
      '.weekly-timetable__sessions > .weekly-timetable__empty:not(.weekly-timetable__error)',
    );
  }

  function noResultsNode(el) {
    let node = el.querySelector('.timetable-filters__no-results');
    if (node) return node;

    node = serverEmptyNode(el) || document.createElement('div');
    node.className = 'weekly-timetable__empty timetable-filters__no-results';
    node.innerHTML = EMPTY_COPY;
    if (!node.parentNode) el.querySelector('.weekly-timetable__sessions')?.appendChild(node);
    return node;
  }

  function updateEmptyState(el, anyVisible, catVal) {
    const retainCategory = !anyVisible && !!catVal;
    const existing = el.querySelector('.timetable-filters__empty-category');

    if (retainCategory) {
      const node = emptyCategory(el);
      node.querySelector('.weekly-timetable__category-name').textContent = catVal;
      node.style.display = '';
    } else if (existing) {
      existing.style.display = 'none';
    }

    noResultsNode(el).style.display = anyVisible || retainCategory ? 'none' : '';
  }

  function collectFilterParams(el) {
    const { tod, spaces } = getSelects(el);
    const values = selectedValues(el);
    const params = {};
    FILTERS.forEach(({ key, param }) => {
      if (values[key]) params[param] = values[key];
    });
    if (tod?.value) params.time_of_day = tod.value;
    if (spaces?.checked) params.free_spaces = '1';
    return params;
  }

  function syncUrl(el) {
    const url = new URL(window.location.href);
    FILTER_PARAMS.forEach(k => url.searchParams.delete(k));
    const params = collectFilterParams(el);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    history.replaceState(null, '', url.toString());
  }

  function setValue(sel, value) {
    if (!sel || !value) return;
    if (![...sel.options].some(o => o.value === value)) return;
    sel.value = value;
    updateSelectState(sel);
  }

  // A bookmarked url can name a combination the centre no longer offers, so
  // each filter is restored against the options the ones before it leave, and
  // anything unsupported is dropped rather than filtered on.
  function restoreFromUrl(el) {
    const { tod, spaces } = getSelects(el);
    const p = new URLSearchParams(window.location.search);

    FILTERS.forEach(({ key, param }) => {
      constrainFilters(el);
      setValue(getSelects(el)[key], p.get(param));
    });

    if (tod && p.get('time_of_day')) {
      tod.value = p.get('time_of_day');
      updateSelectState(tod);
    }

    if (spaces && p.get('free_spaces') === '1') {
      spaces.checked = true;
    }

    applyFilters(el);
  }

  function timetableContainer(el) {
    return el.querySelector('[data-timetable-filters-target="timetable"]');
  }

  function statusNode(el) {
    return el.querySelector('[data-timetable-filters-target="status"]');
  }

  // The picked date has to light up before the sessions arrive, so the
  // highlight is moved here rather than waiting on the response.
  function setActiveDay(el, link) {
    el.querySelectorAll('.timetable-date-nav__day').forEach(day => {
      day.classList.remove('timetable-date-nav__day--active');
      day.removeAttribute('aria-current');
    });
    link.classList.add('timetable-date-nav__day--active');
    link.setAttribute('aria-current', 'date');
  }

  function dateUrlFor(el, link) {
    const newDate = new URL(link.href).searchParams.get('timetables_items_date');
    const url = new URL(window.location.href);
    FILTER_PARAMS.forEach(k => url.searchParams.delete(k));
    if (newDate) url.searchParams.set('timetables_items_date', newDate);
    Object.entries(collectFilterParams(el)).forEach(([k, v]) => url.searchParams.set(k, v));
    return url;
  }

  function selectDate(el, link) {
    const url = dateUrlFor(el, link);
    setActiveDay(el, link);
    history.pushState({}, '', url.toString());
    loadTimetableDate({
      container: timetableContainer(el),
      status: statusNode(el),
      url: url.toString(),
      label: link.dataset.dateLabel || link.textContent.trim(),
      onLoaded: () => applyFilters(el),
    });
  }

  function interceptDateNav(el, signal) {
    el.querySelectorAll('.timetable-date-nav__day').forEach(link => {
      link.addEventListener('click', e => {
        if (e.defaultPrevented) return;
        e.preventDefault();
        selectDate(el, link);
      }, { signal });
    });

    // The date lives in the URL, so going back has to land on that date's
    // sessions rather than the ones already on screen.
    window.addEventListener('popstate', () => window.location.reload(), { signal });
  }

  function resetFilters(el) {
    const { tod, spaces } = getSelects(el);
    FILTERS.forEach(({ key }) => {
      const sel = getSelects(el)[key];
      if (sel) sel.value = '';
      updateSelectState(sel);
    });
    if (tod) tod.value = '';
    if (spaces) spaces.checked = false;
    updateSelectState(tod);
    applyFilters(el);
    syncUrl(el);
  }

  function initFilters(el) {
    if (el._filtersAbort) el._filtersAbort.abort();
    const ctrl = new AbortController();
    el._filtersAbort = ctrl;
    const { signal } = ctrl;

    combinations(el);
    optionSnapshot(el);
    restoreFromUrl(el);
    interceptDateNav(el, signal);

    const { spaces, reset } = getSelects(el);

    [...FILTERS.map(f => f.target), 'timeOfDay'].forEach(target => {
      el.querySelector(`[data-timetable-filters-target="${target}"]`)
        ?.addEventListener('change', (e) => {
          updateSelectState(e.target);
          applyFilters(el);
          syncUrl(el);
        }, { signal });
    });

    spaces?.addEventListener('change', () => {
      applyFilters(el);
      syncUrl(el);
    }, { signal });

    reset?.addEventListener('click', e => {
      e.preventDefault();
      resetFilters(el);
    }, { signal });
  }

  function initAll() {
    document.querySelectorAll('[data-controller="timetable-filters"]').forEach(initFilters);
  }

  document.addEventListener('DOMContentLoaded', initAll);
};
